import type {
  WatchAlertView,
  WatchAlertViews,
} from "@b2bportal/air-price-watch-api";
import type { ReservationWrapper } from "@b2bportal/core-types";
import { Product } from "@b2bportal/purchase-api";

/**
 * Sorts watch alerts chronologically by departure date, with return date as a fallback.
 *
 * Sorting behavior:
 * - Primary: departure date (earliest first)
 * - Secondary: return date (earliest first)
 * - One-way flights (no return date) sort before round-trips with the same departure date
 *
 * @param watches - The watches array to sort
 * @returns A new sorted array of watches
 */
export const sortByDate = (watches: WatchAlertView[]): WatchAlertView[] => {
  return [...watches].sort((a, b) => {
    if (!a.key.value || !b.key.value) return 0;

    const aDepartureDate = a.key.value.departureDate;
    const bDepartureDate = b.key.value.departureDate;

    // Primary sort: departure date
    if (aDepartureDate !== bDepartureDate) {
      return aDepartureDate.localeCompare(bDepartureDate);
    }

    // Secondary sort: return date (handle one-way flights explicitly)
    const aReturnDate = a.key.value.returnDate;
    const bReturnDate = b.key.value.returnDate;

    // If both are one-way (no return date), they're equal
    if (!aReturnDate && !bReturnDate) return 0;
    // One-way flights sort before round-trips with the same departure date
    if (!aReturnDate) return -1;
    if (!bReturnDate) return 1;

    return aReturnDate.localeCompare(bReturnDate);
  });
};

/**
 * Extracts the end date from a reservation wrapper based on product type.
 *
 * Product-specific end dates:
 * - Flights: Return flight departure date (slices[1]) for round trips
 * - Hotels: Check-out date
 * - Cars: Drop-off date (already in wrapper.endDate)
 * - Activities: End date/time
 *
 * @param wrapper - The reservation wrapper
 * @returns The end date string, or undefined if not available
 */
export const getEndDateFromWrapper = (
  wrapper: ReservationWrapper<any> & { endDate?: string }
): string | undefined => {
  // Cars already have endDate in the wrapper
  if (wrapper.endDate) {
    return wrapper.endDate;
  }

  if (!wrapper.reservation) {
    return undefined;
  }

  const reservation = wrapper.reservation;

  // Extract end date based on product type
  switch (wrapper.productType) {
    case Product.Flight: {
      // For flights, check if there's a return slice (slices[1]) for round trips
      const itinerary = reservation?.bookedItinerary?.travelItinerary;
      const slices = itinerary?.slices;

      if (!Array.isArray(slices) || slices.length < 2) {
        return undefined;
      }

      const returnSlice = slices[1];
      const returnSegments = returnSlice?.segments;

      if (Array.isArray(returnSegments) && returnSegments.length > 0) {
        return returnSegments[0]?.scheduledDeparture;
      }

      return undefined;
    }

    case Product.Hotel: {
      // For hotels, use checkout date
      return reservation?.checkOutDate;
    }

    case Product.Homes: {
      // For homes/vacation rentals, use the end of stay date
      return reservation?.stayDates?.until;
    }

    case Product.Activities: {
      // For activities, use end date if available
      return reservation?.endDate || reservation?.endDateTime;
    }

    case Product.Package: {
      // For packages, try to find an end date
      return reservation?.endDate;
    }

    default:
      return undefined;
  }
};

/**
 * Sorts trip wrappers chronologically by start date, with end date as a fallback.
 *
 * Sorting behavior:
 * - Groups current trips (recentFirst=false) before past trips (recentFirst=true)
 * - Within current trips: earliest start date first, then earliest end date
 * - Within past trips: latest start date first, then latest end date
 * - Trips without a sortDate (start date) are pushed to the end (invalid state)
 * - Trips without end dates sort before trips with end dates when start dates match (e.g., one-way trips before round trips)
 *
 * @param wrappers - Array of reservation wrappers to sort
 * @returns A new sorted array
 */
export const sortTripsByDate = <
  T extends ReservationWrapper<unknown> & { endDate?: string },
>(
  wrappers: T[]
): T[] => {
  return [...wrappers].sort((a, b) => {
    // Ensure both wrappers have required fields
    if (!a.sortDate || !b.sortDate) {
      if (!a.sortDate && !b.sortDate) return 0;
      return !a.sortDate ? 1 : -1;
    }

    // First, group by trip category: current trips before past trips
    // Handle undefined recentFirst by treating it as false (current trip)
    const aRecentFirst = a.recentFirst ?? false;
    const bRecentFirst = b.recentFirst ?? false;

    if (aRecentFirst !== bRecentFirst) {
      return aRecentFirst ? 1 : -1;
    }

    // Both trips are in the same category, sort by date
    const recentFirst = aRecentFirst;

    // Extract just the date portion (YYYY-MM-DD) to avoid timezone issues
    // This works for both date-only strings ("2026-04-20") and datetime strings ("2026-04-19T14:00:00-04:00")
    const dateStrA = a.sortDate.split("T")[0];
    const dateStrB = b.sortDate.split("T")[0];

    // Compare date strings lexicographically (YYYY-MM-DD format sorts correctly)
    const primarySort = recentFirst
      ? dateStrB.localeCompare(dateStrA) // Past trips: most recent first
      : dateStrA.localeCompare(dateStrB); // Current trips: soonest first

    // If primary dates are different, use primary sort
    if (primarySort !== 0) {
      return primarySort;
    }

    // Secondary sort: by end date when start dates are the same
    const aEndDate = getEndDateFromWrapper(a);
    const bEndDate = getEndDateFromWrapper(b);

    // If both have no end date, they're equal
    if (!aEndDate && !bEndDate) return 0;
    // Trips without end dates sort before trips with end dates
    if (!aEndDate) return -1;
    if (!bEndDate) return 1;

    // Both have end dates - compare date strings
    const endDateStrA = aEndDate.split("T")[0];
    const endDateStrB = bEndDate.split("T")[0];

    // For upcoming trips (recentFirst=false), earlier end date comes first
    // For past trips (recentFirst=true), later end date comes first
    const endSort = recentFirst
      ? endDateStrB.localeCompare(endDateStrA) // Past trips: latest end date first
      : endDateStrA.localeCompare(endDateStrB); // Current trips: earliest end date first

    if (endSort !== 0) {
      return endSort;
    }

    // Tertiary sort: by product type for stable ordering
    // Order: Flight, Hotel, Ground, Activities, Package, Others
    const productOrder: Record<string, number> = {
      [Product.Flight]: 1,
      [Product.Hotel]: 2,
      [Product.Homes]: 3,
      [Product.Ground]: 4,
      [Product.Activities]: 5,
      [Product.Package]: 6,
    };

    const aProduct = a.productType ? (productOrder[a.productType] ?? 999) : 999;
    const bProduct = b.productType ? (productOrder[b.productType] ?? 999) : 999;

    return aProduct - bProduct;
  });
};

/**
 * Sorts WatchAlertViews data chronologically by departure date, with return date as a fallback.
 * Returns a new object with sorted watches array.
 *
 * @param watchesData - The WatchAlertViews data to sort
 * @returns A new WatchAlertViews object with sorted watches, or undefined if input is undefined
 */
export const sortWatchAlertViewsByDate = (
  watchesData?: WatchAlertViews
): WatchAlertViews | undefined => {
  if (!watchesData?.watches) return watchesData;

  return {
    ...watchesData,
    watches: sortByDate(watchesData.watches),
  };
};
